import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import './Pages.css';

const stripePromise = process.env.REACT_APP_STRIPE_KEY ? loadStripe(process.env.REACT_APP_STRIPE_KEY) : null;

function pad(n) {
  return String(n).padStart(2, '0');
}

// UTC-basic format required by the .ics VEVENT DTSTART/DTEND fields
function toICSDate(date) {
  return `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`;
}

function buildICS(booking) {
  const start = new Date(booking.booking_date);
  const end = new Date(start.getTime() + (booking.duration_minutes || 60) * 60000);
  const descriptionParts = [`Session with ${booking.practitioner_name || booking.client_name}`];
  if (booking.video_link) descriptionParts.push(`Join: ${booking.video_link}`);
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Had Collective//Booking//EN',
    'BEGIN:VEVENT',
    `UID:${booking.uuid}@hadcollective`,
    `DTSTAMP:${toICSDate(new Date())}`,
    `DTSTART:${toICSDate(start)}`,
    `DTEND:${toICSDate(end)}`,
    `SUMMARY:${booking.service_name}`,
    `DESCRIPTION:${descriptionParts.join('\\n')}`,
    booking.video_link ? `LOCATION:${booking.video_link}` : '',
    'END:VEVENT',
    'END:VCALENDAR'
  ].filter(Boolean);
  return lines.join('\r\n');
}

function downloadICS(booking) {
  const blob = new Blob([buildICS(booking)], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${booking.service_name.replace(/\s+/g, '-')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function googleCalendarUrl(booking) {
  const start = new Date(booking.booking_date);
  const end = new Date(start.getTime() + (booking.duration_minutes || 60) * 60000);
  const details = booking.video_link
    ? `Join: ${booking.video_link}`
    : `Session with ${booking.practitioner_name || booking.client_name}`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: booking.service_name,
    dates: `${toICSDate(start)}/${toICSDate(end)}`,
    details,
    location: booking.video_link || ''
  });
  return `https://www.google.com/calendar/render?${params.toString()}`;
}

const STEPS = [
  { key: 'pending', title: 'Booking Requested', desc: 'Waiting on the practitioner to approve' },
  { key: 'awaiting_payment', title: 'Approved - Invoice Sent', desc: 'Waiting on payment' },
  { key: 'confirmed', title: 'Booking Confirmed', desc: 'Payment received' },
  { key: 'completed', title: 'Session Completed', desc: 'This session is now in your history' }
];

function statusStepIndex(status) {
  if (status === 'rejected' || status === 'cancelled') return -1;
  return STEPS.findIndex((s) => s.key === status);
}

function PaymentForm({ apiUrl, token, bookingId, amount, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setLoading(true);
    setError(null);
    try {
      const cardElement = elements.getElement(CardElement);
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement
      });
      if (pmError) {
        setError(pmError.message);
        setLoading(false);
        return;
      }
      const response = await axios.post(
        `${apiUrl}/payments/confirm`,
        { booking_id: bookingId, payment_method_id: paymentMethod.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        onSuccess();
      } else {
        setError('Payment was not successful. Please try another card.');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="form-group">
        <div className="input" style={{ padding: '12px 14px' }}>
          <CardElement options={{ style: { base: { fontSize: '16px' } } }} />
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      <button type="submit" disabled={!stripe || loading} className="btn btn-primary btn-block">
        {loading ? 'Processing...' : `Pay $${parseFloat(amount).toFixed(2)}`}
      </button>
    </form>
  );
}

function BookingDetail({ user, apiUrl, token }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [notePrivate, setNotePrivate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [videoLink, setVideoLink] = useState('');
  const [videoNotes, setVideoNotes] = useState('');

  const isPractitionerView = booking && (booking.practitioner_id === user?.id);
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [bookingRes, notesRes, attachmentsRes] = await Promise.all([
        axios.get(`${apiUrl}/bookings/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/bookings/${id}/notes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${apiUrl}/bookings/${id}/attachments`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setBooking(bookingRes.data);
      setNotes(notesRes.data);
      setAttachments(attachmentsRes.data);
      setVideoLink(bookingRes.data.video_link || '');
      setVideoNotes(bookingRes.data.video_notes || '');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, apiUrl, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const submitReschedule = async (e) => {
    e.preventDefault();
    if (!rescheduleDate) return;
    setActionLoading(true);
    try {
      await axios.patch(
        `${apiUrl}/bookings/${id}/reschedule`,
        { booking_date: new Date(rescheduleDate).toISOString() },
        { headers }
      );
      setShowReschedule(false);
      setRescheduleDate('');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reschedule booking');
    } finally {
      setActionLoading(false);
    }
  };

  const submitVideoDetails = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await axios.patch(`${apiUrl}/bookings/${id}/video`, { video_link: videoLink, video_notes: videoNotes }, { headers });
      setShowVideoForm(false);
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save video details');
    } finally {
      setActionLoading(false);
    }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(`${apiUrl}/bookings/${id}/attachments`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload attachment');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const downloadAttachment = async (attachment) => {
    try {
      const response = await axios.get(
        `${apiUrl}/bookings/${id}/attachments/${attachment.id}/download`,
        { headers, responseType: 'blob' }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.file_name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download attachment');
    }
  };

  const runAction = async (action) => {
    setActionLoading(true);
    try {
      await axios.patch(`${apiUrl}/bookings/${id}/${action}`, {}, { headers });
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} booking`);
    } finally {
      setActionLoading(false);
    }
  };

  const submitNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim()) return;
    try {
      await axios.post(
        `${apiUrl}/bookings/${id}/notes`,
        { note: newNote, is_private: notePrivate },
        { headers }
      );
      setNewNote('');
      setNotePrivate(false);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add note');
    }
  };

  if (loading) return <div className="page-container"><p className="text-muted">Loading session...</p></div>;
  if (error && !booking) return <div className="page-container"><div className="error-message">{error}</div></div>;
  if (!booking) return null;

  const stepIndex = statusStepIndex(booking.status);
  const isRejected = booking.status === 'rejected' || booking.status === 'cancelled';

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginBottom: 8 }}>
            ← Back
          </button>
          <h1>{booking.service_name}</h1>
        </div>
        <span className={`badge badge-${booking.status}`}>{booking.status.replace('_', ' ')}</span>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="grid" style={{ gridTemplateColumns: '1.3fr 1fr', alignItems: 'start' }}>
        <div>
          <div className="card">
            <div className="card-header">Session Details</div>
            <p><strong>With:</strong> {isPractitionerView ? booking.client_name : booking.practitioner_name}</p>
            {!isPractitionerView && booking.practitioner_bio && (
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>{booking.practitioner_bio}</p>
            )}
            <div className="flex-between">
              <p style={{ margin: 0 }}><strong>Date:</strong> {new Date(booking.booking_date).toLocaleString()}</p>
              {isPractitionerView && !isRejected && booking.status !== 'completed' && (
                <button className="btn btn-ghost btn-sm" onClick={() => { setRescheduleDate(''); setShowReschedule(true); }}>
                  Reschedule
                </button>
              )}
            </div>
            <p><strong>Amount:</strong> ${parseFloat(booking.amount).toFixed(2)}</p>
            {booking.service_description && <p className="text-muted">{booking.service_description}</p>}

            {booking.invoice_id && (
              <p style={{ marginBottom: 0 }}>
                <strong>Invoice:</strong> <span className={`badge badge-${booking.invoice_status}`}>{booking.invoice_status}</span>
                {' '}<a href="/invoices">View invoices →</a>
              </p>
            )}

            {(booking.status === 'confirmed' || booking.status === 'completed') && booking.video_link && (
              <div style={{ background: 'var(--color-info-bg)', padding: 12, borderRadius: 10, marginTop: 12 }}>
                <a href={booking.video_link} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm">
                  Join Session
                </a>
                {booking.video_recording_enabled && (
                  <p className="text-muted" style={{ fontSize: '0.78rem', marginTop: 8, marginBottom: 0 }}>
                    This session is scheduled to be recorded.
                  </p>
                )}
                {booking.video_notes && <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: 8, marginBottom: 0 }}>{booking.video_notes}</p>}
              </div>
            )}
            {isPractitionerView && !isRejected && (
              <button className="btn btn-ghost btn-sm" style={{ marginTop: 10 }} onClick={() => setShowVideoForm(true)}>
                {booking.video_link ? 'Edit video link' : '+ Add video conferencing link'}
              </button>
            )}

            {(booking.status === 'confirmed' || booking.status === 'completed') && (
              <div className="flex-between" style={{ marginTop: 12 }}>
                <button className="btn btn-secondary btn-sm" onClick={() => downloadICS(booking)}>Download .ics</button>
                <a className="btn btn-secondary btn-sm" href={googleCalendarUrl(booking)} target="_blank" rel="noopener noreferrer">
                  Add to Google Calendar
                </a>
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-header">Lifecycle</div>
            <div className="timeline">
              {STEPS.map((step, i) => {
                const done = !isRejected && i < stepIndex;
                const active = !isRejected && i === stepIndex;
                const cls = isRejected && i === 0 ? 'rejected' : done ? 'done' : active ? 'active' : '';
                return (
                  <div key={step.key} className={`timeline-step ${cls}`}>
                    <div className="timeline-dot">{done ? '✓' : i + 1}</div>
                    <div className="timeline-content">
                      <div className="timeline-title">{step.title}</div>
                      <div className="timeline-desc">{step.desc}</div>
                    </div>
                  </div>
                );
              })}
              {isRejected && (
                <div className="timeline-step rejected">
                  <div className="timeline-dot">✕</div>
                  <div className="timeline-content">
                    <div className="timeline-title">Booking {booking.status}</div>
                  </div>
                </div>
              )}
            </div>

            {isPractitionerView && booking.status === 'pending' && (
              <div className="flex-between" style={{ marginTop: 12 }}>
                <button className="btn btn-success" disabled={actionLoading} onClick={() => runAction('approve')}>
                  Approve &amp; Send Invoice
                </button>
                <button className="btn btn-danger" disabled={actionLoading} onClick={() => runAction('reject')}>
                  Decline
                </button>
              </div>
            )}
            {isPractitionerView && booking.status === 'confirmed' && (
              <button className="btn btn-secondary" disabled={actionLoading} onClick={() => runAction('complete')} style={{ marginTop: 12 }}>
                Mark Session Completed
              </button>
            )}

            {!isPractitionerView && booking.status === 'awaiting_payment' && stripePromise && (
              <div style={{ marginTop: 16 }}>
                <Elements stripe={stripePromise}>
                  <PaymentForm apiUrl={apiUrl} token={token} bookingId={booking.id} amount={booking.amount} onSuccess={fetchData} />
                </Elements>
              </div>
            )}
          </div>
        </div>

        <div>
        <div className="card">
          <div className="card-header">Session Notes</div>
          <p className="text-muted" style={{ fontSize: '0.8rem', marginTop: -8, marginBottom: 14 }}>
            {isPractitionerView
              ? 'Notes you mark private are only visible to you.'
              : 'Visible to you and your practitioner.'}
          </p>
          {notes.length === 0 ? (
            <p className="empty-state">No notes yet</p>
          ) : (
            notes.map((n) => (
              <div key={n.id} className={`note ${n.is_private ? 'is-private' : ''} ${n.author_role === 'client' ? 'is-client' : ''}`}>
                <div className="note-meta">
                  <span>{n.author_name} {n.is_private && '· private'}</span>
                  <span>{new Date(n.created_at).toLocaleDateString()}</span>
                </div>
                <div className="note-text">{n.note}</div>
              </div>
            ))
          )}
          <form onSubmit={submitNote} style={{ marginTop: 14 }}>
            <div className="form-group">
              <textarea
                className="input"
                rows="3"
                placeholder="Add a note about this session..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
              />
            </div>
            {isPractitionerView && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: '0.85rem' }}>
                <input type="checkbox" checked={notePrivate} onChange={(e) => setNotePrivate(e.target.checked)} />
                Keep this note private (not shared with client)
              </label>
            )}
            <button type="submit" className="btn btn-primary btn-sm">Add Note</button>
          </form>
        </div>

        <div className="card">
          <div className="card-header">Attachments</div>
          {attachments.length === 0 ? (
            <p className="empty-state">No files attached yet</p>
          ) : (
            attachments.map((a) => (
              <div key={a.id} className="flex-between" style={{ padding: '8px 0', borderBottom: '1px solid var(--color-border)' }}>
                <div>
                  <div>{a.file_name}</div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {a.uploaded_by_name} · {new Date(a.created_at).toLocaleDateString()}
                  </div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => downloadAttachment(a)}>Download</button>
              </div>
            ))
          )}
          <label className="btn btn-secondary btn-sm" style={{ marginTop: 14, display: 'inline-flex' }}>
            {uploading ? 'Uploading...' : '+ Add Attachment'}
            <input type="file" onChange={uploadAttachment} disabled={uploading} style={{ display: 'none' }} />
          </label>
        </div>
        </div>
      </div>

      {showReschedule && (
        <div className="modal" onClick={() => setShowReschedule(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Reschedule Session</h2>
              <button className="close-btn" onClick={() => setShowReschedule(false)}>×</button>
            </div>
            <form onSubmit={submitReschedule}>
              <div className="form-group">
                <label>New date &amp; time</label>
                <input
                  type="datetime-local"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  required
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={actionLoading}>
                {actionLoading ? 'Saving...' : 'Save New Time'}
              </button>
            </form>
          </div>
        </div>
      )}

      {showVideoForm && (
        <div className="modal" onClick={() => setShowVideoForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Video Conferencing</h2>
              <button className="close-btn" onClick={() => setShowVideoForm(false)}>×</button>
            </div>
            <form onSubmit={submitVideoDetails}>
              <div className="form-group">
                <label>Meeting Link</label>
                <input
                  type="url"
                  placeholder="https://zoom.us/j/..."
                  value={videoLink}
                  onChange={(e) => setVideoLink(e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  rows="2"
                  placeholder="e.g. Meeting ID, passcode..."
                  value={videoNotes}
                  onChange={(e) => setVideoNotes(e.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary btn-block" disabled={actionLoading}>
                {actionLoading ? 'Saving...' : 'Save'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BookingDetail;
